import React from "react";
import {
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import {
  Box,
  Container,
  Typography,
  Paper,
  Avatar,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  useMediaQuery,
  Grid,
  styled,
} from "@mui/material";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { jwtDecode } from "jwt-decode";

const userPosition = {
  rank: 4,
  currentScore: 87,
  lastWeek: 83,
  lastMonthAvg: 80,
  pointsGain: 4,
};
const DashboardCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  display: "flex",
  flexDirection: "column",
  gap: theme?.spacing(2),
  border: "1px solid #e0e0e0",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    margin: theme.spacing(0, 1),
  },
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: "#fff",
  "& .MuiLinearProgress-bar": {
    borderRadius: 4,
    backgroundColor: "#6366F1",
  },
}));

const PositionBadge = styled(Avatar)(({ theme }) => ({
  backgroundColor: "#6366F1",
  color: "white",
  width: 48,
  height: 48,
  fontSize: "1.25rem",
  fontWeight: "bold",
  [theme.breakpoints.down("sm")]: {
    width: 40,
    height: 40,
    fontSize: "1rem",
  },
}));
function ProgressBar({ isCurrentAgent, responseData }) {
  const positionScore =
    responseData?.lastMonthAgentOverallScore -
    isCurrentAgent?.averagePerformance;

  const token = localStorage.getItem("token");
  const decodedToken = token ? jwtDecode(token) : "";

  const currentUser = responseData?.performanceLeaderborad?.find(
    (agent) => agent?.agentId === decodedToken?.agentId
  );
  const firstUser = responseData?.performanceLeaderborad?.find(
    (item) => item?.rank === 1
  );
  const thirdUser = responseData?.performanceLeaderborad?.find(
    (item) => item?.rank === 3
  );

  let infoText = "";
  let pointDiff = 0;

  if (currentUser && firstUser) {
    const currentRank = currentUser.rank;
    const currentScore = Math.round(currentUser?.averagePerformance);
    const firstScore = Math.round(firstUser?.averagePerformance);

    if (currentRank === 1) {
      infoText = "🎉 Congratulations! You're at the top!";
    } else if (currentRank === 2 || currentRank === 3) {
      pointDiff = firstScore - currentScore;
      infoText = `You're just ${pointDiff} ${
        pointDiff !== 1 ? "s" : ""
      } away from moving up to 1st place!`;
    } else if (thirdUser) {
      const thirdScore = Math.round(thirdUser.averagePerformance);
      pointDiff = thirdScore - currentScore + 1;
      infoText = `You're just  ${pointDiff} ${
        pointDiff !== 1 ? "s" : ""
      } away from moving up to top 3!`;
    }
  }
  return (
    <>
      {/* Current Score */}
      <DashboardCard elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            fontWeight="600"
            color="text.primary"
            sx={{ fontSize: "1.125rem" }}
          >
            Your Position
          </Typography>
          <PositionBadge>{isCurrentAgent?.rank}</PositionBadge>
        </Box>
        {/* Motivational Message */}
        <Box sx={{ p: 2, backgroundColor: "#EEF0FE", borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              // mb: 1,
            }}
          >
            <Typography variant="body1" fontWeight="500" color="text.primary">
              Current Score
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="#6366F1">
              {isCurrentAgent?.averagePerformance
                ? Math.round(isCurrentAgent?.averagePerformance)
                : "0"}
            </Typography>
          </Box>
          <StyledLinearProgress
            variant="determinate"
            value={
              isCurrentAgent?.averagePerformance
                ? Math.round(isCurrentAgent?.averagePerformance)
                : 0
            }
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              0
            </Typography>
            <Typography variant="caption" color="text.secondary">
              100
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ textAlign: "center" }}>
            {infoText.includes("Congratulations") ? (
              infoText
            ) : (
              <>
                {infoText.split(String(pointDiff))[0]}
                <span style={{ color: "#6366F1", fontWeight: 600 }}>
                  {pointDiff} point
                </span>
                {infoText.split(String(pointDiff))[1]}
              </>
            )}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Historical Performance */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Last Week
              </Typography>
              <Typography variant="body2" fontWeight="600" color="text.primary">
                {responseData?.lastMonthAgentOverallScore
                  ? Math.round(responseData?.lastMonthAgentOverallScore)
                  : 0}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={
                responseData?.lastMonthAgentOverallScore
                  ? Math.round(responseData?.lastMonthAgentOverallScore)
                  : 0
              }
              sx={{
                height: 7,
                borderRadius: 3,
                backgroundColor: "#f0f0f0",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  backgroundColor: "#9e9e9e",
                },
              }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Last Month Avg.
              </Typography>
              <Typography variant="body2" fontWeight="600" color="text.primary">
                {responseData?.lastWeekAgentOverallScore
                  ? Math.round(responseData?.lastWeekAgentOverallScore)
                  : 0}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={
                responseData?.lastWeekAgentOverallScore
                  ? Math.round(responseData?.lastWeekAgentOverallScore)
                  : 0
              }
              sx={{
                height: 7,
                borderRadius: 3,
                backgroundColor: "#f0f0f0",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  backgroundColor: "#9e9e9e",
                },
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              p: 1,
            }}
          >
            {Math.round(positionScore) > 0 ? (
              <>
                <TrendingUpIcon sx={{ color: "#4caf50", fontSize: 20 }} />
                <Typography variant="body2" color="#4caf50" fontWeight="600">
                  +{Math.round(positionScore)} points up since last week
                </Typography>
              </>
            ) : Math.round(positionScore) < 0 ? (
              <>
                <TrendingDownIcon sx={{ color: "#f44336", fontSize: 20 }} />
                <Typography variant="body2" color="#f44336" fontWeight="600">
                  {Math.round(positionScore)} points down since last week
                </Typography>
              </>
            ) : Math.round(positionScore) == 0 ? (
              <>
                <RemoveCircleOutlineIcon
                  sx={{ color: "#9e9e9e", fontSize: 20 }}
                />

                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="600"
                >
                  No change from last week
                </Typography>
              </>
            ) : (
              ""
            )}
          </Box>
        </Box>
      </DashboardCard>
    </>
  );
}

export default ProgressBar;
